import type { ICarouselCreativeItemFormValuesModel } from '@/editor/shared/components/forms/creatives/models/carousel-creative-form-values';
import type {
  PlatformType} from '@/editor/shared/constants/available-platforms';
import {
  AvailablePlatformsEnum,
  platformsToCropImage
} from '@/editor/shared/constants/available-platforms';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { TemplateType } from '@/editor/shared/constants/template-type.enum';
import type {
  ICreativeFormsStatePerPlatform,
  ICreativeStepState} from '@/editor/store/reducers/creative-step.slice';
import {
  addedMediaOnDynamic,
  removeImageOnVideo,
  removeMediaOnDynamic,
  removeMediaOnSingle,
  setImageOnVideo,
  setMediaOnCarouselItem,
  setMediaOnSingle,
} from '@/editor/store/reducers/creative-step.slice';
import type { IMediaLibraryDrawerState} from '@/editor/store/reducers/modals-drawers.slice';
import { setImageCropper } from '@/editor/store/reducers/modals-drawers.slice';
import type { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import { getValueFromLs, setKeyValuePairToLS } from '@/shared/services/localstorage';
import { useAppDispatch } from '@/store/hooks/redux';
import toast, { Toast } from 'react-hot-toast';

const useHandleImageClick = (
  isImageSelectedByPlatform: (platform: PlatformType) => boolean,
  drawerState: IMediaLibraryDrawerState,
  creativeState: ICreativeStepState
) => {
  const dispatch = useAppDispatch();

  const handleImageClick = async (platformOption: PlatformType, image: IMediaLibraryImageModel): Promise<void> => {
    if (isImageSelectedByPlatform(platformOption)) {
      removeImage(platformOption, image);
      return;
    }

    if (platformOption == AvailablePlatformsEnum.BIDTHEATRE) {
      setKeyValuePairToLS(EDITOR_LS_CONSTANTS.TEMPLATE_TYPE, TemplateType.REGULAR_TEMPLATE);
      setKeyValuePairToLS(EDITOR_LS_CONSTANTS.IMAGE_FOR_VIDEO, 'image');
    }
    if (ruleForDynamicAndCarousel(platformOption)) {
      toast.error('You can select only 5 images for dynamic and carousel creatives');
      return;
    }
    addingImage(platformOption, image);
  };

  const addingImage = (platformOption: string, image: IMediaLibraryImageModel): void => {
    const platform = platformOption as PlatformType;

    const creativeType =
      platform === AvailablePlatformsEnum.DELTA ||
      (platform === AvailablePlatformsEnum.BIDTHEATRE &&
        drawerState.creativeType !== CreativeTypesEnum.DYNAMIC &&
        drawerState.creativeType !== CreativeTypesEnum.VIDEO) ||
      platform === AvailablePlatformsEnum.BOARDS
        ? CreativeTypesEnum.SINGLE
        : drawerState.creativeType;

    switch (creativeType) {
      case CreativeTypesEnum.CAROUSEL:
        if (!checkDimensionsOfImage(platform, image)) {
          return;
        }
        updateCarouselItemImageIfNeeded(platform, image);
        break;
      case CreativeTypesEnum.DYNAMIC:
        if (!checkDimensionsOfImage(platform, image)) {
          return;
        }
        dispatch(addedMediaOnDynamic({ media: image, platform }));
        break;
      case CreativeTypesEnum.SINGLE:
        dispatch(setMediaOnSingle({ media: image, platform }));
        break;
      case CreativeTypesEnum.VIDEO:
        dispatch(setImageOnVideo({ media: image, platform }));
        break;
    }
  };

  const removeImage = (platformOption: string, image: IMediaLibraryImageModel): void => {
    const platform = platformOption as PlatformType;
    const creativeType =
      platform === AvailablePlatformsEnum.DELTA ||
      (platform === AvailablePlatformsEnum.BIDTHEATRE &&
        drawerState.creativeType !== CreativeTypesEnum.DYNAMIC &&
        drawerState.creativeType !== CreativeTypesEnum.VIDEO) ||
      platform === AvailablePlatformsEnum.BOARDS
        ? CreativeTypesEnum.SINGLE
        : drawerState.creativeType;

    switch (creativeType) {
      case CreativeTypesEnum.DYNAMIC:
        dispatch(removeMediaOnDynamic({ platform, media: image }));
        break;
      case CreativeTypesEnum.SINGLE:
        dispatch(removeMediaOnSingle({ platform }));
        break;
      case CreativeTypesEnum.CAROUSEL:
        updateCarouselItemImageIfNeeded(platform, null);
        break;
      case CreativeTypesEnum.VIDEO:
        dispatch(removeImageOnVideo({ platform }));
        break;
    }
  };

  const checkDimensionsOfImage = (platform: PlatformType, image: IMediaLibraryImageModel): boolean => {
    const platformConfigToCrop = platformsToCropImage.find((el) => el.platform === platform);

    if (
      platformConfigToCrop &&
      (image.width !== platformConfigToCrop.width || image.height !== platformConfigToCrop.height)
    ) {
      toast.error(
        `Du kan ikke velge dette bildet ${platform.toUpperCase()} ${drawerState.creativeType?.toUpperCase()} \n\n Velg et bilde med dimensjonene: ${platformConfigToCrop.height}px x ${platformConfigToCrop.width}px`
      );

      dispatch(
        setImageCropper({
          isOpened: true,
          image: image,
          platform: platform,
          propertyInternalId: getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID),
          widthToCrop: platformConfigToCrop.width,
          heightToCrop: platformConfigToCrop.height,
        })
      );

      return false;
    }

    return true;
  };

  const updateCarouselItemImageIfNeeded = (
    platformOption: PlatformType,
    media: IMediaLibraryImageModel | null
  ): void => {
    if (drawerState.carouselItemIndex !== undefined) {
      dispatch(
        setMediaOnCarouselItem({
          media,
          index: drawerState.carouselItemIndex,
          platform: platformOption as PlatformType,
        })
      );
    }
    if (media === null && drawerState.carouselItemIndex !== undefined) {
      toast.error('This images is already selected!');
    }
  };

  const ruleForDynamicAndCarousel = (platform: PlatformType): boolean => {
    const form = creativeState.creativeFormsState[platform] as ICreativeFormsStatePerPlatform;
    const medias: IMediaLibraryImageModel[] | ICarouselCreativeItemFormValuesModel[] =
      drawerState.creativeType === CreativeTypesEnum.DYNAMIC
        ? form.dynamic?.creativeMedias
        : form.carousel?.creativeItems;
    return medias?.length >= 5;
  };

  return { handleImageClick };
};

export default useHandleImageClick;
